import React, { useState, useRef, useContext, useEffect } from 'react';
import dig from 'object-dig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import SendIcon from '@material-ui/icons/Send';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CloseIcon from '@material-ui/icons/Close';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import Avatar from '@material-ui/core/Avatar';
import Fab from '@material-ui/core/Fab';
import Link from '@material-ui/core/Link';

import LinearProgress from '~/components/LinearProgress';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Button from '~/components/Button';
import ModalDelete from '~/components/ModalDelete';

import toInt from '~/utils/Number/toInt';
import Request from '~/providers/Request';
import AttendantWebSocket from '~/contexts/AttendantWebSocket';
import { URL } from '~/config/uri';
import isPresent from '~/utils/isPresent';
import { showPerson, chats } from '~/config/routes';

import InputMessage from './InputMessage';
import ChannelData from './ChannelData';
import ChatHistory from './ChatHistory';

const useStyles = makeStyles(() => ({
  messageAreaMessage: {
    height: '60vh',
    overflowY: 'auto',
  },
  root: {
    display: 'flex',
  },
}));

function Edit({ history }) {
  const classes = useStyles();
  const { attendant, webSocket } = useContext(AttendantWebSocket);
  const { id } = history.location.route.getParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [loadingClose, setLoadingClose] = useState(false);
  const [modal, setModal] = useState(false);

  const messagesRef = useRef();
  const [chat, setChat] = useState(null);

  const [user_id, setUser_id] = useState(null);
  const [messages, setMessages] = useState([]);

  const closeChat = async () => {
    try {
      await Request.del(`/admin/chats/${id}`);
      toast.success(t('chats.extras.close_success'));
      setModal(false);
      setLoadingClose(false);
      history.push(chats.spec);
    } catch (e) {
      setLoadingClose(false);
      toast.error(t('chats.extras.close_fail'));
    }
  };

  const sendMessage = async (message_input) => {
    webSocket.emit('message', {
      type: 'chatNewMessage',
      payload: {
        to: chat.user_to.uuid,
        source: 'private',
        type: 'text',
        message: message_input,
        chat_id: chat.id,
        specialty_id: chat.specialty_id,
      },
    });
    scrollElementId();
  };

  const scrollElementId = () => {
    const div = messagesRef.current;

    if (div) {
      div.scrollTop = div.scrollHeight;
    }
  };

  const fetchMessages = async () => {
    setLoading(true);

    const response = await Request.get(
      `/admin/chats/${id}/messages`,
      {},
      { normalizeJSON: false }
    );
    await Request.put(
      `/admin/chats/${id}/read_messages`,
      {},
      {},
      { normalizeJSON: false }
    );
    setLoading(false);

    setMessages(response.data);
    scrollElementId();
  };

  useEffect(() => {
    if (attendant && webSocket) {
      if (isPresent(messages)) {
        if (messages.length > 0 && messages[messages.length - 1].id) {
          webSocket.emit('message', {
            type: 'chatUpdateMessage',
            payload: {
              action: 'read',
              messageID: messages[messages.length - 1].id,
              userFromId: parseInt(attendant.id),
            },
          });
        }
      }
    }
  }, [attendant, webSocket, messages]);

  useEffect(() => {
    if (attendant && webSocket && id) {
      webSocket.on('message', async ({ type, payload }) => {
        if (type === 'chatNewMessage' && toInt(payload.chat_id) === toInt(id)) {
          await fetchMessages();
          webSocket.emit('message', {
            type: 'chatUpdateMessage',
            payload: {
              action: 'read',
              messageID: payload.id,
              userFromId: parseInt(attendant.id),
            },
          });
        }
      });
    }
  }, [attendant, webSocket, id]);

  useEffect(() => {
    async function fetchChat(id) {
      setLoading(true);
      Request.get(`/admin/chats/${id}`, {})
        .then((response) => {
          setLoading(false);
          setChat(response.data);
          setUser_id(response.data.user_to.id);
          fetchMessages();
        })
        .catch((e) => {
          console.error(e);
          setLoading(false);
        });
    }
    if (id) {
      fetchChat(id);
    }
  }, [id]);

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.root}>
            <Avatar
              src={`${URL}/public/user_face/${dig(
                chat,
                'user_to',
                'uuid'
              )}.jpg?size=140`}
            />

            <Typography variant="h6" style={{ marginLeft: '10px' }}>
              <Link
                target="_blank"
                rel="noopener"
                href={showPerson.reverse({ id: dig(chat, 'user_to', 'id') })}
              >
                {dig(chat, 'user_to', 'name')}
                <OpenInNewIcon />
              </Link>
            </Typography>
          </div>
          <div style={{ marginTop: '10px' }} className={classes.root}>
            <Typography variant="subtitle1">
              {t('chats.attributes.subject')}: {dig(chat, 'subject')}
            </Typography>

            <Button
              style={{ marginLeft: 10 }}
              disabled={chat?.is_closed}
              size="small"
              color="error"
              onClick={() => setModal(true)}
            >
              {t('chats.extras.close')}
            </Button>
          </div>
        </GridItem>
      </GridContainer>

      <Divider />

      <GridContainer>
        <GridItem xs={12}>
          <LinearProgress loading={loading} />
          <List className={classes.messageAreaMessage} ref={messagesRef}>
            <ChatHistory id={id} user_id={user_id} />
            <ChannelData messages={messages} user_id={user_id} />
          </List>
          <LinearProgress loading={loading} />
        </GridItem>
      </GridContainer>
      <Divider />
      <GridContainer style={{ padding: '20px' }}>
        <GridItem xs={11}>
          <InputMessage is_closed={chat?.is_closed} sendMessage={sendMessage} />
        </GridItem>
        <GridItem xs={1} align="right">
          <Fab onClick={sendMessage} disabled={chat?.is_closed} color="primary">
            <SendIcon />
          </Fab>
        </GridItem>
      </GridContainer>

      <ModalDelete
        modal={modal}
        icon={CloseIcon}
        isLoading={loadingClose}
        message={t('chats.extras.close_confirm')}
        textDelete={t('chats.extras.close')}
        onClose={() => {
          setModal(false);
        }}
        deleteAction={closeChat}
      />
    </>
  );
}

export default Edit;
